
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { Button, Input, Row, Spacer, Stack, Text } from '@client/shared/design';
import { cx } from '@client/shared/lib/cx.ts';
import { MAX_UNAVAILABILITY_REASON_LENGTH } from '@shared/index';
import { AnchoredCard } from '@client/shared/design';
import styles from './ListingDetails.module.css';

export type DetailTone = 'mine' | 'other' | 'blocked';

const toneClass: Record<DetailTone, string | undefined> = {
  mine: styles.detailCardMine,
  other: styles.detailCardOther,
  blocked: styles.detailCardBlocked,
};

export interface EventDetailCardProps {
  anchorRef: RefObject<HTMLElement | null>;
  tone: DetailTone;
  eyebrow: string;
  ariaLabel: string;
  onClose: () => void;
  children: ReactNode;
}

export function EventDetailCard({
  anchorRef,
  tone,
  eyebrow,
  ariaLabel,
  onClose,
  children,
}: EventDetailCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[role="listbox"]')) return;
      e.preventDefault();
      onClose();
    };
    const onDocDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (cardRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDocDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDocDown);
    };
  }, [anchorRef, onClose]);

  return (
    <AnchoredCard
      ref={cardRef}
      anchor={{ ref: anchorRef }}
      className={cx(styles.detailCard, toneClass[tone])}
      gap={6}
      cardWidth={300}
      flipRoom={200}
      flipUp={6}
      role="dialog"
      aria-modal="false"
      aria-label={ariaLabel}
    >
      <Stack gap={2}>
        <Row gap={2} align="center">
          <span className={cx(styles.detailDot, toneClass[tone])} aria-hidden="true" />
          <Text as="span" size="xs" tone="muted" weight="semibold" className={styles.popoverEyebrow}>
            {eyebrow}
          </Text>
        </Row>
        {children}
      </Stack>
    </AnchoredCard>
  );
}

function FailLine() {
  return (
    <Text size="xs" tone="danger">
      That didn’t go through. Try again.
    </Text>
  );
}

export interface BookingDetailBodyProps {
  title: string;
  timeRange: string;
  meta: string;
  note?: string;
  canCancel: boolean;
  onCancel: () => Promise<void>;
  onClose: () => void;
}

export function BookingDetailBody({
  title,
  timeRange,
  meta,
  note,
  canCancel,
  onCancel,
  onClose,
}: BookingDetailBodyProps) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  async function cancel() {
    setBusy(true);
    setError(false);
    try {
      await onCancel();
    } catch {
      setError(true);
      setBusy(false);
    }
  }

  return (
    <Stack gap={1}>
      <Text size="sm" weight="semibold">
        {title}
      </Text>
      <Text size="sm" className="tnum">
        {timeRange}
      </Text>
      <Text size="xs" tone="muted">
        {meta}
      </Text>

      {note && (
        <Text size="xs" tone="soft">
          {note}
        </Text>
      )}

      {error && <FailLine />}

      {canCancel &&
        (confirming ? (
          <Stack gap={2}>
            <Text size="sm" weight="medium">
              Cancel this open house?
            </Text>
            <Row gap={2} align="center">
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(false)}>
                Keep it
              </Button>
              <Spacer />
              <Button
                variant="primary"
                size="sm"
                tone="danger"
                loading={busy}
                onClick={() => void cancel()}
              >
                Yes, cancel
              </Button>
            </Row>
          </Stack>
        ) : (
          <Row gap={2} align="center">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
            <Spacer />
            <Button variant="ghost" size="sm" tone="danger" onClick={() => setConfirming(true)}>
              Cancel open house
            </Button>
          </Row>
        ))}

      {!canCancel && (
        <Row align="center">
          <Spacer />
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </Row>
      )}
    </Stack>
  );
}

export interface BlockDetailBodyProps {
  reason: string | null;
  timeRange: string;
  meta: ReactNode;
  canEdit: boolean;
  onEditReason: (reason: string) => Promise<void>;
  onRemove: () => Promise<void>;
  onClose: () => void;
}

export function BlockDetailBody({
  reason,
  timeRange,
  meta,
  canEdit,
  onEditReason,
  onRemove,
  onClose,
}: BlockDetailBodyProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(reason ?? '');
  const [busy, setBusy] = useState<'save' | 'remove' | null>(null);
  const [error, setError] = useState(false);
  const reasonId = useId();

  const hasReason = Boolean(reason && reason.trim() !== '');

  async function save() {
    setBusy('save');
    setError(false);
    try {
      await onEditReason(draft);
      setEditing(false);
    } catch {
      setError(true);
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    setBusy('remove');
    setError(false);
    try {
      await onRemove();
    } catch {
      setError(true);
      setBusy(null);
    }
  }

  return (
    <Stack gap={1}>
      {editing ? (
        <Input
          autoFocus
          id={reasonId}
          value={draft}
          maxLength={MAX_UNAVAILABILITY_REASON_LENGTH}
          placeholder="Reason (optional)"
          aria-label="Block reason"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            }
          }}
        />
      ) : hasReason ? (
        <Text size="sm" weight="semibold" className={styles.blockPopoverReason}>
          {reason}
        </Text>
      ) : (
        <Text size="sm" tone="soft">
          No reason given
        </Text>
      )}

      <Text size="sm" className="tnum">
        {timeRange}
      </Text>
      <Text size="xs" tone="muted">
        {meta}
      </Text>

      {error && <FailLine />}

      {canEdit ? (
        editing ? (
          <Row gap={2} align="center">
            <Button
              variant="ghost"
              size="sm"
              disabled={busy !== null}
              onClick={() => {
                setDraft(reason ?? '');
                setEditing(false);
                setError(false);
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" size="sm" loading={busy === 'save'} onClick={() => void save()}>
              Save
            </Button>
          </Row>
        ) : (
          <Row gap={2} align="center">
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              {hasReason ? 'Edit reason' : 'Add reason'}
            </Button>
            <Spacer />
            <Button
              variant="ghost"
              size="sm"
              tone="danger"
              loading={busy === 'remove'}
              onClick={() => void remove()}
            >
              Remove block
            </Button>
          </Row>
        )
      ) : (
        <Row align="center">
          <Spacer />
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </Row>
      )}
    </Stack>
  );
}
